/**
 * The Outreach screen's Calls tab — what it draws, from the two reads it has. Import-free so jest can exercise
 * it, and mirrored 1:1 by iOS / Android `OutreachCalls.state` (same states, same names).
 *
 * Whether Alfred is connected is the CONNECTOR row's `connected` (false whenever Alfred has no number). The
 * number itself comes from the setup read (`/api/connectors/alfred-setup`, native-data `alfred-setup-state`),
 * asked only once the row says connected: the tab never buys anything.
 */

/** The setup read, as far as the tab needs it. */
export type CallsNumberLoad =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; phoneNumber: string | null; displayPhoneNumber: string | null };

export type CallsState =
  /** The connectors read has not answered yet. */
  | { kind: 'loading' }
  /** It answered with no Alfred entry (outside the connector's rollout): the pitch, and nothing to press. */
  | { kind: 'noEntry' }
  /** Alfred is not set up: the pitch and Connect. */
  | { kind: 'connect' }
  | { kind: 'numberLoading' }
  | { kind: 'numberFailed' }
  /** `display` is shown; `e164` is what Copy copies. */
  | { kind: 'number'; display: string; e164: string };

export function callsState(input: {
  connectorsLoaded: boolean;
  alfred: { connected: boolean } | null | undefined;
  number: CallsNumberLoad;
}): CallsState {
  if (!input.connectorsLoaded) return { kind: 'loading' };
  if (!input.alfred) return { kind: 'noEntry' };
  if (!input.alfred.connected) return { kind: 'connect' };
  if (input.number.status === 'loading') return { kind: 'numberLoading' };
  if (input.number.status === 'failed') return { kind: 'numberFailed' };
  const e164 = input.number.phoneNumber?.trim();
  // The row said connected, but the read holds no number: Connect again, which reuses the site's number.
  if (!e164) return { kind: 'connect' };
  return { kind: 'number', display: input.number.displayPhoneNumber?.trim() || e164, e164 };
}
